import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft,
  Plus, 
  Edit, 
  Trash2, 
  DollarSign,
  Calendar,
  User,
  TrendingUp,
  Percent
} from 'lucide-react';
import { 
  getCampaignById, 
  getCampaignDonations, 
  addCampaignDonation, 
  deleteCampaignDonation,
  getAllUsers
} from '../services/api';
import { FundraisingCampaign, CampaignDonation, User as UserType } from '../types';

const CampaignDetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const BackButton = styled.button`
  background: #f8f9fa;
  color: #333;
  border: 2px solid #e1e5e9;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AddDonationButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

const CampaignInfo = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const CampaignTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
`;

const CampaignDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const ProgressContainer = styled.div`
  margin-bottom: 25px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: #e1e5e9;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 15px;
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  width: ${props => Math.min(props.$percentage, 100)}%;
  transition: width 0.3s ease;
`;

const ProgressStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ProgressText = styled.div`
  display: flex;
  gap: 20px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const ProgressPercentage = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #667eea;
`;

const CampaignMeta = styled.div`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
`;

const DonationsSection = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DonationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const DonationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    background: #f0f2ff;
  }
`;

const DonationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const DonationMember = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const DonationDate = styled.div`
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const DonationAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2e7d32;
`;

const DonationActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant: 'delete' }>`
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  background: #ffebee;
  color: #d32f2f;
  
  &:hover {
    background: #ffcdd2;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<FundraisingCampaign | null>(null);
  const [donations, setDonations] = useState<CampaignDonation[]>([]);
  const [members, setMembers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    memberId: '',
    amount: 0,
    donationDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    if (id) {
      loadCampaignData();
    }
  }, [id]);

  const loadCampaignData = async () => {
    if (!id) return;
    
    try {
      const [campaignData, donationsData, membersData] = await Promise.all([
        getCampaignById(id),
        getCampaignDonations(id),
        getAllUsers()
      ]);
      
      setCampaign(campaignData);
      setDonations(donationsData);
      setMembers(membersData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading campaign data:', error);
      setLoading(false);
    }
  };

  const getMemberName = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    return member ? `${member.name} ${member.lastName}` : 'לא ידוע';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getProgressPercentage = (current: number, target: number) => {
    if (target === 0) return 0;
    return Math.round((current / target) * 100);
  };

  const handleAddDonation = () => {
    setFormData({
      memberId: '',
      amount: 0,
      donationDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setIsModalOpen(true);
  };

  const handleSaveDonation = async () => {
    if (!id) return;
    
    try {
      await addCampaignDonation({
        campaignId: id,
        memberId: formData.memberId,
        amount: formData.amount,
        donationDate: new Date(formData.donationDate),
        notes: formData.notes,
        createdBy: 'current-user-id' // This should come from auth context
      });
      
      await loadCampaignData();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error adding donation:', error);
    }
  };

  const handleDeleteDonation = async (donationId: string, amount: number) => {
    if (!id) return;
    
    if (window.confirm('האם אתה בטוח שברצונך למחוק את התרומה?')) {
      try {
        await deleteCampaignDonation(donationId, id, amount);
        await loadCampaignData();
      } catch (error) {
        console.error('Error deleting donation:', error);
      }
    }
  };

  if (loading) {
    return (
      <CampaignDetailContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </CampaignDetailContainer>
    );
  }

  if (!campaign) {
    return (
      <CampaignDetailContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>קמפיין לא נמצא</h2>
          <p>הקמפיין המבוקש לא קיים במערכת</p>
        </div>
      </CampaignDetailContainer>
    );
  }

  return (
    <CampaignDetailContainer>
      <Header>
        <BackButton onClick={() => navigate('/campaigns')}>
          <ArrowLeft size={20} />
          חזור לקמפיינים
        </BackButton>
        <Title>
          <DollarSign size={28} />
          {campaign.title}
        </Title>
        <AddDonationButton onClick={handleAddDonation}>
          <Plus size={20} />
          הוסף תרומה
        </AddDonationButton>
      </Header>

      <CampaignInfo>
        <CampaignTitle>{campaign.title}</CampaignTitle>
        <CampaignDescription>{campaign.description}</CampaignDescription>
        
        <ProgressContainer>
          <ProgressBar>
            <ProgressFill $percentage={getProgressPercentage(campaign.currentAmount, campaign.targetAmount)} />
          </ProgressBar>
          <ProgressStats>
            <ProgressText>
              <span>נאסף: {formatCurrency(campaign.currentAmount)}</span>
              <span>מטרה: {formatCurrency(campaign.targetAmount)}</span>
            </ProgressText>
            <ProgressPercentage>
              {getProgressPercentage(campaign.currentAmount, campaign.targetAmount)}%
            </ProgressPercentage>
          </ProgressStats>
        </ProgressContainer>

        <CampaignMeta>
          <MetaItem>
            <Calendar size={16} />
            התחלה: {(() => {
              try {
                let d;
                if (campaign.startDate && typeof campaign.startDate === 'object' && 'toDate' in campaign.startDate) {
                  d = campaign.startDate.toDate();
                } else if (campaign.startDate instanceof Date) {
                  d = campaign.startDate;
                } else {
                  d = new Date(campaign.startDate);
                }
                return isNaN(d.getTime()) ? 'לא מוגדר' : d.toLocaleDateString('he-IL');
              } catch { return 'לא מוגדר'; }
            })()}
          </MetaItem>
          {campaign.endDate && (
            <MetaItem>
              <Calendar size={16} />
              סיום: {(() => {
                try {
                  let d;
                  if (campaign.endDate && typeof campaign.endDate === 'object' && 'toDate' in campaign.endDate) {
                    d = campaign.endDate.toDate();
                  } else if (campaign.endDate instanceof Date) {
                    d = campaign.endDate;
                  } else {
                    d = new Date(campaign.endDate);
                  }
                  return isNaN(d.getTime()) ? 'לא מוגדר' : d.toLocaleDateString('he-IL');
                } catch { return 'לא מוגדר'; }
              })()}
            </MetaItem>
          )}
          <MetaItem>
            <TrendingUp size={16} />
            סטטוס: {campaign.isActive ? 'פעיל' : 'לא פעיל'}
          </MetaItem>
        </CampaignMeta>
      </CampaignInfo>

      <DonationsSection>
        <SectionTitle>
          <User size={24} />
          תרומות ({donations.length})
        </SectionTitle>

        {donations.length === 0 ? (
          <EmptyState>
            <User size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
            <h3>אין תרומות עדיין</h3>
            <p>לחץ על "הוסף תרומה" כדי להתחיל</p>
          </EmptyState>
        ) : (
          <DonationsList>
            {donations.map((donation) => (
              <DonationItem key={donation.id}>
                <DonationInfo>
                  <DonationMember>
                    {getMemberName(donation.memberId)}
                  </DonationMember>
                  <DonationDate>
                    <Calendar size={14} />
                    {(() => {
                      try {
                        let d: any = donation.donationDate;
                        if (d && typeof d === 'object' && 'toDate' in d) {
                          d = d.toDate();
                        } else if (!(d instanceof Date)) {
                          d = new Date(d);
                        }
                        return isNaN(d.getTime()) ? 'תאריך לא תקין' : d.toLocaleDateString('he-IL');
                      } catch {
                        return 'תאריך לא תקין';
                      }
                    })()}
                    {donation.notes && ` - ${donation.notes}`}
                  </DonationDate>
                </DonationInfo>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <DonationAmount>
                    {formatCurrency(donation.amount)}
                  </DonationAmount>
                  <DonationActions>
                    <ActionButton
                      variant="delete"
                      onClick={() => handleDeleteDonation(donation.id, donation.amount)}
                      title="מחק תרומה"
                    >
                      <Trash2 size={16} />
                    </ActionButton>
                  </DonationActions>
                </div>
              </DonationItem>
            ))}
          </DonationsList>
        )}
      </DonationsSection>

      <Modal isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>הוסף תרומה</ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>מתפלל *</Label>
            <Select
              value={formData.memberId}
              onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}
              required
            >
              <option value="">בחר מתפלל</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name} {member.lastName}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>סכום התרומה (₪) *</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
              required
              min="1"
            />
          </FormGroup>

          <FormGroup>
            <Label>תאריך התרומה</Label>
            <Input
              type="date"
              value={formData.donationDate}
              onChange={(e) => setFormData({ ...formData, donationDate: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>הערות</Label>
            <TextArea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="הערות נוספות..."
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSaveDonation}>
              הוסף תרומה
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </CampaignDetailContainer>
  );
};

export default CampaignDetail;
